/**
 * OverflowRow — 한 줄에 다 들어가지 않는 배지 묶음을 `⋯`로 접었다가 펼친다.
 *
 * 시험지 편집 모달의 「담은 문제」와 학생 허브의 읽기 전용 미리보기가 같은 동작을
 * 써야 하므로 별도 부품으로 둔다. 펼친 상태에서는 줄바꿈을 허용하고, 접힌 상태에서만
 * 실제 overflow를 측정한다. 내용이 비동기로 보강되는 경우에는 contentKey를 바꿔 다시 잰다.
 */
import { useEffect, useRef, useState } from 'react';

function OverflowRow({ children, contentKey = '', collapsible = true, label = '개념' }) {
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState(false);
  const shown = open || !collapsible;

  useEffect(() => {
    const el = boxRef.current;
    if (el == null || shown) return undefined;
    const measure = () => setOver(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [shown, contentKey]);

  return (
    <div className="flex min-w-0 items-start gap-1.5">
      <div
        ref={boxRef}
        className={`flex min-w-0 flex-1 items-center gap-1.5 ${shown ? 'flex-wrap' : 'overflow-hidden'}`}
      >
        {children}
      </div>
      {collapsible && (over || open) && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          aria-label={open ? `${label} 접기` : `${label} 전부 보기`}
          title={open ? '접기' : '전부 보기'}
          className="mt-px shrink-0 rounded-full border border-gray-200 px-1.5 py-0 text-[10px] leading-4 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
        >
          {open ? '접기' : '⋯'}
        </button>
      )}
    </div>
  );
}

export default OverflowRow;
